import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, formatApiError, useAuth } from "@/lib/auth";
import {
  ArrowLeft, Wallet, DollarSign, Plus, Trash2, Pencil, X,
  Loader2, Zap, Activity, PiggyBank, AlertCircle, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import DeleteProjectDialog from "@/components/admin/DeleteProjectDialog";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const fmtUsd = (v) => {
  const n = Number(v || 0);
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtCredits = (v) => `${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

// Partner-visible pricing (buffer-preserving model):
//   Top-ups store credits 1:1 at $0.1667 base rate.
//   Usage rows are marked up 1.25× internally before being displayed,
//   then billed at $0.1667 per displayed credit (owner keeps a 25%
//   raw-credit buffer). The word "markup" never appears on this page.
const BILLING_UNIT_COST = 500 / 3000;                      // $0.1667 per credit shown

const BillingPage = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [daily, setDaily] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Topup form
  const [topupCredits, setTopupCredits] = useState("");
  const [topupCost, setTopupCost] = useState("");
  const [topupNote, setTopupNote] = useState("");
  // Usage form
  const [usageCredits, setUsageCredits] = useState("");
  const [usageNote, setUsageNote] = useState("");
  // Project picker for Usage — feeds the /admin/roi cost board
  const [projects, setProjects] = useState([]);
  const [usageProjectId, setUsageProjectId] = useState("");   // "" = no project, "__new__" = inline create
  const [newProjName, setNewProjName] = useState("");
  const [newProjFee, setNewProjFee] = useState("");
  const [creatingProject, setCreatingProject] = useState(false);
  // Ledger project scope — remembers last-viewed project across reloads
  const [scopeProjectId, setScopeProjectId] = useState(() => {
    try { return localStorage.getItem("nex_billing_scope_v1") || ""; } catch { return ""; }
  });
  // Inline "+ Add project" flow from the scope selector
  const [scopeAddOpen, setScopeAddOpen] = useState(false);
  const [scopeNewName,  setScopeNewName]  = useState("");
  const [scopeNewFee,   setScopeNewFee]   = useState("");
  const [scopeAddBusy,  setScopeAddBusy]  = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Retroactive sweep
  const [recalcBusy, setRecalcBusy] = useState(false);

  const load = async (projectId = scopeProjectId) => {
    const q = projectId ? `&project_id=${encodeURIComponent(projectId)}` : "";
    try {
      const [s, e, d, p] = await Promise.all([
        apiClient.get(`/admin/billing/summary?_=1${q}`),
        apiClient.get(`/admin/billing/entries?limit=200${q}`),
        apiClient.get(`/admin/billing/daily?days=30${q}`),
        apiClient.get("/admin/billing/projects"),
      ]);
      setSummary(s.data);
      setEntries(e.data.entries || []);
      setDaily(d.data.series || []);
      setProjects(p.data.projects || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(scopeProjectId); }, [scopeProjectId]);
  useEffect(() => {
    try { localStorage.setItem("nex_billing_scope_v1", scopeProjectId || ""); } catch { /* quota */ }
  }, [scopeProjectId]);

  // Inline "+ Add new project" from the scope selector — same shape as
  // the Log Usage flow but auto-selects the created project as the scope.
  const createScopeProject = async () => {
    const name = scopeNewName.trim();
    if (!name) return toast.error("Project name required");
    setScopeAddBusy(true);
    try {
      const { data } = await apiClient.post("/admin/billing/projects", {
        name,
        monthly_fee: scopeNewFee ? Number(scopeNewFee) : 0,
      });
      const created = data.project;
      setProjects((p) => [{ id: created.id, name: created.name, job_id: created.job_id }, ...p]);
      setScopeProjectId(created.id);
      setScopeNewName(""); setScopeNewFee(""); setScopeAddOpen(false);
      toast.success(`Project "${created.name}" ready · scoped view active`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setScopeAddBusy(false); }
  };

  const addTopup = async (e) => {
    e.preventDefault();
    if (!topupCredits || Number(topupCredits) <= 0) return toast.error("Enter a positive credit amount");
    setBusy(true);
    try {
      await apiClient.post("/admin/billing/topup", {
        credits: Number(topupCredits),
        cost_usd: topupCost ? Number(topupCost) : null,
        note: topupNote,
      });
      toast.success(`+${topupCredits} credits added`);
      setTopupCredits(""); setTopupCost(""); setTopupNote("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const addUsage = async (e) => {
    e.preventDefault();
    if (!usageCredits || Number(usageCredits) <= 0) return toast.error("Enter a positive usage amount");
    if (usageProjectId === "__new__") return toast.error("Save the new project first");
    setBusy(true);
    try {
      await apiClient.post("/admin/billing/usage", {
        actual_credits: Number(usageCredits),
        note: usageNote,
        project_id: usageProjectId || null,
      });
      const projLabel = projects.find((p) => p.id === usageProjectId)?.name;
      toast.success(projLabel ? `Logged ${usageCredits} credits · ${projLabel}` : `Logged ${usageCredits} credits`);
      setUsageCredits(""); setUsageNote("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const createProject = async () => {
    const name = newProjName.trim();
    if (!name) return toast.error("Project name required");
    setCreatingProject(true);
    try {
      const { data } = await apiClient.post("/admin/billing/projects", {
        name,
        monthly_fee: newProjFee ? Number(newProjFee) : 0,
      });
      const created = data.project;
      setProjects((p) => [{ id: created.id, name: created.name, job_id: created.job_id }, ...p]);
      setUsageProjectId(created.id);
      setNewProjName(""); setNewProjFee("");
      toast.success(`Project "${created.name}" ready · feeds cost board`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCreatingProject(false); }
  };

  const removeEntry = async (id) => {
    if (!window.confirm("Delete this ledger entry?")) return;
    try {
      await apiClient.delete(`/admin/billing/entries/${id}`);
      toast.success("Entry deleted");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // Partner-only edit modal for a ledger entry
  const [editing, setEditing] = useState(null);
  const [e_note, setENote] = useState("");
  const [e_credits, setECredits] = useState("");
  const [e_cost, setECost] = useState("");
  const [e_project, setEProject] = useState("");
  const [busyEdit, setBusyEdit] = useState(false);

  const openEditEntry = (row) => {
    setEditing(row);
    setEProject(row.project_id || "");
    setENote(row.note || "");
    setECredits(String(row.actual_credits ?? row.display_credits ?? ""));
    setECost(String(row.cost_usd ?? row.amount_usd ?? ""));
  };

  const saveEntryEdit = async (ev) => {
    ev.preventDefault();
    if (!editing) return;
    setBusyEdit(true);
    try {
      const payload = { note: e_note };
      if (e_project !== (editing.project_id || "")) payload.project_id = e_project;
      if (e_credits !== "") payload.actual_credits = Number(e_credits);
      if (e_cost !== "") {
        payload.amount_usd = Number(e_cost);
      }
      await apiClient.patch(`/admin/billing/entries/${editing.id}`, payload);
      toast.success("Entry updated");
      setEditing(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyEdit(false); }
  };

  const recalculateHistory = async () => {
    if (!window.confirm("Recompute credits + cost_usd on every past ledger row using the current formula? Safe to run multiple times.")) return;
    setRecalcBusy(true);
    try {
      const { data } = await apiClient.post("/admin/billing/recalculate");
      toast.success(`History settled · ${data.updated_usage_rows} usage rows, ${data.updated_topup_rows} top-ups`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRecalcBusy(false); }
  };

  const balancePct = useMemo(() => {
    if (!summary || !summary.total_purchased_credits) return 0;
    const bal = summary.current_balance_credits;
    return Math.max(0, Math.min(100, (bal / summary.total_purchased_credits) * 100));
  }, [summary]);

  const isOverdrawn = summary && summary.current_balance_credits < 0;
  const isLowBalance = summary && summary.total_purchased_credits > 0 && summary.current_balance_credits >= 0 && summary.current_balance_credits < 20;

  return (
    <div className="min-h-screen bg-[#0B0F19] p-6 md:p-10">
      <div className="max-w-6xl mx-auto">
        <Link to="/admin" className="mono-body text-[11px] text-nex-chrome hover:text-nex-gold inline-flex items-center gap-2 mb-6">
          <ArrowLeft size={14} /> Back to Console
        </Link>

        <div className="flex flex-col gap-4 mb-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="mono-label mb-2">// BILLING &amp; USAGE</div>
              <h1 className="serif-head text-3xl md:text-4xl">
                Credits Ledger
                {scopeProjectId && (
                  <span className="ml-3 text-lg text-nex-gold align-middle">
                    · {projects.find((p) => p.id === scopeProjectId)?.name || "Project"}
                  </span>
                )}
              </h1>
              <p className="text-nex-chrome mt-2 text-sm">
                Rate <span className="text-nex-gold">${BILLING_UNIT_COST.toFixed(4)}</span> per credit
                · pool of 3,000 credits per <span className="text-nex-gold">$500</span> top-up
                {scopeProjectId && (
                  <span className="ml-2 text-nex-muted">· usage scoped to this project · balance reflects the shared pool</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={recalculateHistory}
                disabled={recalcBusy}
                data-testid="billing-recalculate-btn"
                className="tick-btn-secondary text-xs !py-2 !px-3 disabled:opacity-50"
                title="One-time sweep: recompute credits + cost_usd on every past ledger row using the current formula."
              >
                {recalcBusy
                  ? <><Loader2 size={13} className="animate-spin" /> Recalculating</>
                  : <><RefreshCw size={13} /> Recalculate History</>}
              </button>
            </div>
          </div>

          {/* Project scope selector — funnels every KPI, chart, and entry
              row to the selected project. Choose "All Projects" for the
              platform-wide view. Add new projects inline as we grow. */}
          <div
            className="card-industrial corner-tick p-3 flex flex-col md:flex-row md:items-center gap-3 md:gap-4"
            data-testid="billing-scope-bar"
          >
            <div className="mono-label text-nex-gold whitespace-nowrap">// VIEW</div>
            <div className="flex flex-wrap items-center gap-2 flex-1">
              <button
                type="button"
                onClick={() => setScopeProjectId("")}
                data-testid="billing-scope-all"
                className={
                  scopeProjectId === ""
                    ? "text-xs px-3 py-1.5 border border-nex-gold/60 bg-nex-gold/10 text-nex-gold font-mono uppercase tracking-[0.14em]"
                    : "text-xs px-3 py-1.5 border border-white/10 bg-white/[0.02] text-nex-chrome hover:text-nex-silver font-mono uppercase tracking-[0.14em] transition"
                }
              >
                All Projects
              </button>
              {projects.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setScopeProjectId(p.id)}
                  data-testid={`billing-scope-${p.id}`}
                  className={
                    scopeProjectId === p.id
                      ? "text-xs px-3 py-1.5 border border-nex-gold/60 bg-nex-gold/10 text-nex-gold font-mono uppercase tracking-[0.14em] whitespace-nowrap"
                      : "text-xs px-3 py-1.5 border border-white/10 bg-white/[0.02] text-nex-chrome hover:text-nex-silver font-mono uppercase tracking-[0.14em] transition whitespace-nowrap"
                  }
                >
                  {p.name}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setScopeAddOpen((x) => !x)}
                data-testid="billing-scope-add-toggle"
                className="text-xs px-3 py-1.5 border border-dashed border-nex-gold/40 bg-transparent text-nex-gold/80 hover:text-nex-gold hover:bg-nex-gold/5 font-mono uppercase tracking-[0.14em] inline-flex items-center gap-1 transition"
              >
                <Plus size={12} /> Add project
              </button>
              {scopeProjectId && projects.some((p) => p.id === scopeProjectId) && (
                <button
                  type="button"
                  onClick={() => setDeleteOpen(true)}
                  data-testid="billing-scope-delete-toggle"
                  className="text-xs px-3 py-1.5 border border-dashed border-red-400/40 bg-transparent text-red-300/80 hover:text-red-200 hover:bg-red-500/10 font-mono uppercase tracking-[0.14em] inline-flex items-center gap-1 transition"
                >
                  <Trash2 size={12} /> Delete project
                </button>
              )}
            </div>
          </div>

          {deleteOpen && (
            <DeleteProjectDialog
              project={projects.find((p) => p.id === scopeProjectId)}
              projects={projects}
              onClose={() => setDeleteOpen(false)}
              onDeleted={() => { setDeleteOpen(false); setScopeProjectId(""); load(""); }}
            />
          )}

          {scopeAddOpen && (
            <div className="card-industrial corner-tick p-4 flex flex-col sm:flex-row gap-3" data-testid="billing-scope-add-form">
              <div className="flex-1">
                <label className="mono-label block mb-1.5">Project name</label>
                <input
                  data-testid="billing-scope-new-name"
                  value={scopeNewName}
                  onChange={(e) => setScopeNewName(e.target.value)}
                  className="input-industrial"
                  placeholder="e.g. Nexengines, De'Fontain'e"
                  autoFocus
                />
              </div>
              <div className="sm:w-48">
                <label className="mono-label block mb-1.5">Monthly fee (optional)</label>
                <input
                  data-testid="billing-scope-new-fee"
                  type="number" step="0.01" min="0"
                  value={scopeNewFee}
                  onChange={(e) => setScopeNewFee(e.target.value)}
                  className="input-industrial"
                  placeholder="For ROI margin"
                />
              </div>
              <div className="flex items-end gap-2">
                <button
                  type="button"
                  onClick={createScopeProject}
                  disabled={scopeAddBusy}
                  data-testid="billing-scope-new-save"
                  className="tick-btn-primary disabled:opacity-50"
                >
                  {scopeAddBusy ? <Loader2 size={13} className="animate-spin" /> : <><Plus size={13} /> Save</>}
                </button>
                <button
                  type="button"
                  onClick={() => { setScopeAddOpen(false); setScopeNewName(""); setScopeNewFee(""); }}
                  className="px-3 py-2 border border-white/10 text-nex-chrome hover:text-nex-silver text-xs"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-20"><Loader2 className="animate-spin text-nex-gold mx-auto" /></div>
        ) : !summary ? (
          <div className="card-industrial corner-tick p-10 text-center">
            <AlertCircle className="text-red-300 mx-auto mb-3" size={26} />
            <h2 className="serif-head text-xl mb-2">Couldn&apos;t load billing data</h2>
            <p className="text-nex-chrome text-sm mb-6">Please check your connection and try again.</p>
            <button onClick={load} className="tick-btn-secondary">Retry</button>
          </div>
        ) : (
          <>
            {/* 4 KEY METRICS */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="billing-metrics">
              <MetricCard
                icon={<PiggyBank size={18} />}
                label="Purchased"
                value={fmtCredits(summary.total_purchased_credits)}
                sub={fmtUsd(summary.total_paid_usd) + " paid"}
                testid="metric-purchased"
              />
              <MetricCard
                icon={<Activity size={18} />}
                label="Used"
                value={fmtCredits(summary.total_used_credits)}
                sub="credits consumed"
                accent="silver"
                testid="metric-used"
              />
              <MetricCard
                icon={<Wallet size={18} />}
                label="Balance"
                value={fmtCredits(summary.current_balance_credits)}
                sub={isOverdrawn
                  ? <span className="text-red-300">Overdrawn — top up now</span>
                  : `${balancePct.toFixed(0)}% remaining`}
                accent={isOverdrawn ? "red" : (isLowBalance ? "red" : "gold")}
                testid="metric-balance"
              />
              <MetricCard
                icon={<DollarSign size={18} />}
                label="Dollar Burn"
                value={fmtUsd(summary.total_burn_usd)}
                sub="only what we've used"
                testid="metric-burn"
              />
            </div>

            {/* Balance warning */}
            {(isLowBalance || isOverdrawn) && (
              <div className="border border-red-500/40 bg-red-500/10 p-4 mb-8 flex items-center gap-3">
                <AlertCircle className="text-red-300" size={18} />
                <div className="text-red-200 text-sm">
                  {isOverdrawn ? "Balance is overdrawn — usage has exceeded purchases. Add a top-up now." : "Balance is running low. Add a top-up below to keep the pool funded."}
                </div>
              </div>
            )}

            {/* DAILY BURN CHART */}
            <div data-testid="billing-daily-chart" className="card-industrial corner-tick p-6 md:p-8 mb-10">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="mono-label mb-1">// DAILY BURN · LAST 30 DAYS</div>
                  <h2 className="serif-head text-xl">Cost per day</h2>
                </div>
                <div className="mono-body text-[10px] text-nex-muted">${BILLING_UNIT_COST.toFixed(4)} per credit</div>
              </div>
              {daily.length === 0 ? (
                <div className="text-center py-16">
                  <Zap className="text-nex-muted mx-auto mb-3" size={26} />
                  <p className="text-nex-chrome text-sm">No usage yet. Log some usage below to see the chart populate.</p>
                </div>
              ) : (
                <div className="h-72" data-testid="billing-chart-canvas">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={daily} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                      <XAxis dataKey="date" stroke="#64748B" fontSize={11} tickFormatter={(v) => v.slice(5)} />
                      <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `$${v}`} />
                      <Tooltip
                        contentStyle={{ background: "#0f1524", border: "1px solid #1E293B", color: "#E2E8F0", fontSize: 12 }}
                        formatter={(value, name) => name === "cost_usd" ? [fmtUsd(value), "Cost"] : [fmtCredits(value), "Credits"]}
                      />
                      <Bar dataKey="cost_usd" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* ENTRY FORMS */}
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {/* Topup */}
              <form onSubmit={addTopup} className="card-industrial corner-tick p-6" data-testid="billing-topup-form">
                <div className="flex items-center gap-2 mb-4">
                  <Plus className="text-nex-gold" size={16} />
                  <div className="mono-label">// ADD TOP-UP</div>
                </div>
                <h3 className="serif-head text-lg mb-4">Record a credit purchase</h3>
                <div className="space-y-4">
                  <div>
                    <label className="mono-label block mb-1.5">Credits purchased</label>
                    <input data-testid="topup-credits" type="number" step="0.01" min="0" value={topupCredits} onChange={(e) => setTopupCredits(e.target.value)} className="input-industrial" placeholder="e.g. 250" required />
                  </div>
                  <div>
                    <label className="mono-label block mb-1.5">Cost paid (USD, optional)</label>
                    <input data-testid="topup-cost" type="number" step="0.01" min="0" value={topupCost} onChange={(e) => setTopupCost(e.target.value)} className="input-industrial" placeholder="Leave blank to auto-calc at $0.1667/credit" />
                  </div>
                  <div>
                    <label className="mono-label block mb-1.5">Note (optional)</label>
                    <input data-testid="topup-note" value={topupNote} onChange={(e) => setTopupNote(e.target.value)} className="input-industrial" placeholder="e.g. Feb top-up · Stripe receipt #123" />
                  </div>
                  <button type="submit" disabled={busy} data-testid="topup-submit" className="tick-btn-primary w-full justify-center disabled:opacity-50">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <><Plus size={16} /> Add Top-up</>}
                  </button>
                </div>
              </form>

              {/* Usage */}
              <form onSubmit={addUsage} className="card-industrial corner-tick p-6" data-testid="billing-usage-form">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="text-nex-gold" size={16} />
                  <div className="mono-label">// LOG USAGE</div>
                </div>
                <h3 className="serif-head text-lg mb-1">Record credits consumed</h3>
                <p className="mono-body text-[10px] text-nex-muted mb-4">Tag a project to feed the <span className="text-nex-gold">/admin/roi</span> cost board.</p>
                <div className="space-y-4">
                  <div>
                    <label className="mono-label block mb-1.5">Project</label>
                    <select
                      data-testid="usage-project"
                      value={usageProjectId}
                      onChange={(e) => setUsageProjectId(e.target.value)}
                      className="input-industrial w-full"
                    >
                      <option value="">-- No project (shared / platform) --</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                      <option value="__new__">+ Add new project…</option>
                    </select>
                  </div>
                  {usageProjectId === "__new__" && (
                    <div className="border border-nex-gold/30 bg-nex-gold/5 p-3 space-y-3" data-testid="new-project-inline">
                      <div className="mono-label text-nex-gold">// NEW PROJECT</div>
                      <div>
                        <label className="mono-label block mb-1.5">Project name</label>
                        <input
                          data-testid="new-project-name"
                          value={newProjName}
                          onChange={(ev) => setNewProjName(ev.target.value)}
                          className="input-industrial"
                          placeholder="e.g. Acme Bakery Retainer"
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className="mono-label block mb-1.5">Monthly fee (USD, optional)</label>
                        <input
                          data-testid="new-project-fee"
                          type="number" step="0.01" min="0"
                          value={newProjFee}
                          onChange={(ev) => setNewProjFee(ev.target.value)}
                          className="input-industrial"
                          placeholder="For ROI margin math"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={createProject}
                          disabled={creatingProject}
                          data-testid="new-project-save"
                          className="tick-btn-primary flex-1 justify-center disabled:opacity-50"
                        >
                          {creatingProject ? <Loader2 size={14} className="animate-spin" /> : <><Plus size={14} /> Save Project</>}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setUsageProjectId(""); setNewProjName(""); setNewProjFee(""); }}
                          data-testid="new-project-cancel"
                          className="px-3 py-2 border border-white/10 text-nex-chrome hover:text-nex-silver"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="mono-label block mb-1.5">Actual credits consumed</label>
                    <input data-testid="usage-credits" type="number" step="0.01" min="0" value={usageCredits} onChange={(e) => setUsageCredits(e.target.value)} className="input-industrial" placeholder="raw credits from Emergent pool" required />
                  </div>
                  <div>
                    <label className="mono-label block mb-1.5">Note (optional)</label>
                    <input data-testid="usage-note" value={usageNote} onChange={(e) => setUsageNote(e.target.value)} className="input-industrial" placeholder="e.g. GPT-5.2 bulk brief re-scoring" />
                  </div>
                  <button type="submit" disabled={busy} data-testid="usage-submit" className="tick-btn-primary w-full justify-center disabled:opacity-50">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <><Zap size={16} /> Log Usage</>}
                  </button>
                </div>
              </form>
            </div>

            {/* LEDGER TABLE */}
            <div className="card-industrial corner-tick p-6 md:p-8" data-testid="billing-ledger-table">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="mono-label mb-1">// LEDGER · MOST RECENT FIRST</div>
                  <h2 className="serif-head text-xl">All entries</h2>
                </div>
                <div className="mono-body text-[11px] text-nex-muted">{entries.length} entries</div>
              </div>
              {entries.length === 0 ? (
                <div className="text-center py-10 text-nex-chrome text-sm">No entries yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b border-white/10">
                        <th className="mono-label pb-3 pr-4">When</th>
                        <th className="mono-label pb-3 pr-4">Type</th>
                        <th className="mono-label pb-3 pr-4">Project</th>
                        <th className="mono-label pb-3 pr-4 text-right">Credits</th>
                        <th className="mono-label pb-3 pr-4 text-right">USD</th>
                        <th className="mono-label pb-3 pr-4">Note</th>
                        <th className="mono-label pb-3 pr-4">By</th>
                        <th className="pb-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((e) => (
                        <tr key={e.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                          <td className="py-3 pr-4 mono-body text-[11px] text-nex-chrome whitespace-nowrap">
                            {new Date(e.occurred_at).toLocaleString()}
                          </td>
                          <td className="py-3 pr-4">
                            <span className={`mono-label px-2 py-1 text-[10px] ${e.type === "topup" ? "bg-nex-gold/15 text-nex-gold" : "bg-white/5 text-nex-silver"}`}>
                              {e.type === "topup" ? "TOP-UP" : "USAGE"}
                            </span>
                          </td>
                          <td className="py-3 pr-4 mono-body text-[11px]">
                            {e.project_name ? (
                              <span className="px-2 py-0.5 border border-nex-gold/25 bg-nex-gold/5 text-nex-gold whitespace-nowrap">{e.project_name}</span>
                            ) : (
                              <span className="text-nex-muted">—</span>
                            )}
                          </td>
                          <td className="py-3 pr-4 text-right text-nex-silver">{fmtCredits(e.display_credits)}</td>
                          <td className={`py-3 pr-4 text-right ${e.type === "topup" ? "text-nex-silver" : "text-nex-gold"}`}>
                            {e.type === "topup" ? "−" : "+"}{fmtUsd(e.cost_usd)}
                          </td>
                          <td className="py-3 pr-4 text-nex-chrome truncate max-w-xs">{e.note || "—"}</td>
                          <td className="py-3 pr-4 mono-body text-[11px] text-nex-muted">{e.created_by?.split("@")[0]}</td>
                          <td className="py-3">
                            {user?.is_partner && (
                              <button
                                onClick={() => openEditEntry(e)}
                                data-testid={`entry-edit-${e.id}`}
                                className="text-nex-muted hover:text-nex-gold p-1 mr-1"
                                aria-label="Edit entry"
                                title="Edit"
                              >
                                <Pencil size={14} />
                              </button>
                            )}
                            <button
                              onClick={() => removeEntry(e.id)}
                              data-testid={`entry-delete-${e.id}`}
                              className="text-nex-muted hover:text-red-400 p-1"
                              aria-label="Delete entry"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {editing && (
        <>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[75]" onClick={() => setEditing(null)} />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[80] w-full max-w-md px-4" data-testid="edit-entry-modal">
            <div className="bg-nex-panel border border-nex-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="serif-head text-xl text-nex-silver">Edit Ledger Entry</h3>
                <button onClick={() => setEditing(null)} className="text-nex-muted hover:text-nex-silver" data-testid="edit-entry-close"><X size={18} /></button>
              </div>
              <form onSubmit={saveEntryEdit} className="space-y-3">
                {editing.type === "usage" && (
                  <label className="block">
                    <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Project</div>
                    <select value={e_project} onChange={(ev) => setEProject(ev.target.value)} className="input-industrial w-full" data-testid="edit-entry-project">
                      <option value="">-- No project (shared / platform) --</option>
                      {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </label>
                )}
                <label className="block">
                  <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Actual credits</div>
                  <input type="number" step="0.01" value={e_credits} onChange={(ev) => setECredits(ev.target.value)} className="input-industrial w-full" data-testid="edit-entry-credits" />
                </label>
                <label className="block">
                  <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Cost (USD)</div>
                  <input type="number" step="0.01" value={e_cost} onChange={(ev) => setECost(ev.target.value)} className="input-industrial w-full" data-testid="edit-entry-cost" />
                </label>
                <label className="block">
                  <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Note</div>
                  <input type="text" value={e_note} onChange={(ev) => setENote(ev.target.value)} className="input-industrial w-full" data-testid="edit-entry-note" />
                </label>
                <button type="submit" disabled={busyEdit} className="tick-btn-primary w-full justify-center" data-testid="edit-entry-save">
                  {busyEdit ? <Loader2 size={13} className="animate-spin" /> : "Save changes"}
                </button>
                <p className="text-[11px] text-amber-300/80 text-center leading-relaxed">
                  Display credits will auto-recompute from actual × 1.25 markup.
                </p>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const MetricCard = ({ icon, label, value, sub, accent = "silver", testid }) => {
  const accentClass = accent === "gold"
    ? "text-nex-gold" : accent === "red"
    ? "text-red-300"
    : "text-nex-silver";
  return (
    <div className="card-industrial corner-tick p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="mono-label">{label}</div>
        <div className={accentClass}>{icon}</div>
      </div>
      <div className={`serif-head text-3xl ${accentClass}`}>{value}</div>
      <div className="mono-body text-[10px] text-nex-muted mt-2">{sub}</div>
    </div>
  );
};

export default BillingPage;
